import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Container,
  Menu,
  MenuItem,
  IconButton,
  Avatar,
  Divider,
  Typography,
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EditIcon from "@mui/icons-material/Edit";
import HomeIcon from "@mui/icons-material/Home";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LogoutIcon from "@mui/icons-material/Logout";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { tokens } from "../theme/tokens";
import { authAPI } from "../API/Auth";

const formatBalanceInr = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `₹${Number(value).toLocaleString("en-IN")}`;
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [userInitial, setUserInitial] = useState("");
  const [walletBalanceText, setWalletBalanceText] = useState("…");
  const isLoggedIn = localStorage.getItem("isLoggedIn");

  useEffect(() => {
    const updateUserInitial = () => {
      if (isLoggedIn) {
        const userName = localStorage.getItem("userName");
        if (userName) {
          const initial = userName.trim().charAt(0).toUpperCase();
          setUserInitial(initial);
        }
      }
    };

    updateUserInitial();
    window.addEventListener("storage", updateUserInitial);
    return () => window.removeEventListener("storage", updateUserInitial);
  }, [isLoggedIn]);

  useEffect(() => {
    let cancelled = false;
    const loadBalance = async () => {
      if (!isLoggedIn || isLoggedIn !== "true") {
        setWalletBalanceText("—");
        return;
      }
      const cidRaw = localStorage.getItem("clientId");
      const clientId = cidRaw ? parseInt(cidRaw, 10) : NaN;
      if (!cidRaw || Number.isNaN(clientId)) {
        setWalletBalanceText("—");
        return;
      }
      setWalletBalanceText("…");
      try {
        const res = await authAPI.getClientBalance(clientId);
        const ok =
          res &&
          String(res.status || "").toLowerCase() === "success" &&
          (res.statusCode === 200 || res.statuscode === 200);
        if (cancelled) return;
        if (ok && res.data) {
          const b = res.data.balanceAmount;
          setWalletBalanceText(b == null ? "—" : formatBalanceInr(b));
        } else {
          setWalletBalanceText("—");
        }
      } catch {
        if (!cancelled) setWalletBalanceText("—");
      }
    };
    loadBalance();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, location.pathname]);

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleLogout = () => {
    localStorage.clear();
    handleProfileClose();
    navigate("/login");
  };

  const isMasterUser = localStorage.getItem("isMasterUser") === "true";

  const menuItems = [
    { icon: <HomeIcon fontSize="small" />, text: "Home", path: "/" },
    { icon: <EditIcon fontSize="small" />, text: "Change Password", path: "/change-password" },
    ...(isMasterUser
      ? [
          {
            icon: <AdminPanelSettingsIcon fontSize="small" />,
            text: "Admin Panel",
            path: "/admin",
          },
        ]
      : []),
    {
      icon: <ReceiptIcon fontSize="small" />,
      text: "Booking History",
      path: "/transaction-history",
    },
  ];

  const menuPaperSx = {
    mt: 1.5,
    minWidth: 240,
    borderRadius: `${tokens.radiusMd}px`,
    border: `1px solid ${tokens.border}`,
    boxShadow: tokens.shadowLg,
    overflow: "hidden",
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.92)",
        backdropFilter: "blur(12px)",
        borderBottom: `1px solid ${tokens.border}`,
        color: tokens.text,
      }}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            justifyContent: "space-between",
            minHeight: { xs: 72, md: 80 },
            py: 1,
            gap: 2,
          }}
        >
          <Link
            to="/"
            style={{
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <Box
              component="img"
              src="/images/Wordmark.svg"
              alt="TripTrip India"
              sx={{
                height: { xs: 28, md: 30 },
                width: "auto",
                display: "block",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                display: { xs: "none", sm: "block" },
                color: tokens.textMuted,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontSize: "0.65rem",
                borderLeft: `1px solid ${tokens.border}`,
                pl: 1.5,
                lineHeight: 1.2,
              }}
            >
              B2B portal
            </Typography>
          </Link>

          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            {isLoggedIn ? (
              <>
                <Box
                  aria-label="Wallet balance"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: `${tokens.radiusXl}px`,
                    border: `1px solid ${tokens.borderStrong}`,
                    bgcolor: tokens.surfaceAlt,
                    maxWidth: 200,
                  }}
                >
                  <AccountBalanceWalletIcon sx={{ fontSize: 22, color: tokens.accent, flexShrink: 0 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: tokens.brand,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {walletBalanceText}
                  </Typography>
                </Box>
                <Button
                  onClick={handleProfileClick}
                  variant="outlined"
                  startIcon={<PersonOutlineIcon />}
                  endIcon={
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: tokens.brand,
                        fontSize: "0.8rem",
                        fontWeight: 700,
                      }}
                    >
                      {userInitial || "?"}
                    </Avatar>
                  }
                  sx={{
                    borderColor: tokens.borderStrong,
                    color: tokens.text,
                    borderRadius: `${tokens.radiusXl}px`,
                    px: 2,
                    py: 0.75,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: tokens.accent,
                      bgcolor: tokens.accentSoft,
                    },
                  }}
                >
                  Account
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleProfileClose}
                  slotProps={{
                    paper: { sx: menuPaperSx },
                  }}
                  transformOrigin={{ horizontal: "right", vertical: "top" }}
                  anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                >
                  {menuItems.map((item) => (
                    <MenuItem
                      key={item.text}
                      onClick={() => {
                        handleProfileClose();
                        navigate(item.path);
                      }}
                      sx={{
                        py: 1.25,
                        gap: 1.5,
                        color: tokens.text,
                        "&:hover": { bgcolor: tokens.surfaceAlt },
                      }}
                    >
                      <Box sx={{ color: tokens.accent }}>{item.icon}</Box>
                      {item.text}
                    </MenuItem>
                  ))}
                  <Divider sx={{ my: 0.5 }} />
                  <MenuItem
                    onClick={handleLogout}
                    sx={{
                      py: 1.25,
                      gap: 1.5,
                      color: tokens.error,
                      "&:hover": { bgcolor: tokens.errorSoft },
                    }}
                  >
                    <LogoutIcon fontSize="small" />
                    Log out
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                component={Link}
                to="/login"
                variant="contained"
                color="primary"
                sx={{
                  borderRadius: `${tokens.radiusXl}px`,
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                }}
              >
                Sign in
              </Button>
            )}
          </Box>

          <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            {isLoggedIn ? (
              <Box
                aria-label="Wallet balance"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: `${tokens.radiusMd}px`,
                  border: `1px solid ${tokens.border}`,
                  bgcolor: tokens.surfaceAlt,
                  maxWidth: 120,
                }}
              >
                <AccountBalanceWalletIcon sx={{ fontSize: 18, color: tokens.accent, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: tokens.brand, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {walletBalanceText}
                </Typography>
              </Box>
            ) : null}
            <IconButton
              size="large"
              onClick={handleMobileMenuOpen}
              aria-label="Open menu"
              sx={{ color: tokens.brand }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          <Menu
            anchorEl={mobileMenuAnchor}
            open={Boolean(mobileMenuAnchor)}
            onClose={handleMobileMenuClose}
            slotProps={{
              paper: {
                sx: {
                  ...menuPaperSx,
                  width: "min(100vw - 32px, 320px)",
                },
              },
            }}
          >
            {isLoggedIn ? (
              menuItems
                .concat({
                  icon: <LogoutIcon fontSize="small" />,
                  text: "Log Out",
                  path: "/login",
                })
                .map((item) => (
                  <MenuItem
                    key={item.text}
                    onClick={() => {
                      handleMobileMenuClose();
                      if (item.text === "Log Out") {
                        handleLogout();
                      } else {
                        navigate(item.path);
                      }
                    }}
                    sx={{
                      py: 1.25,
                      gap: 1.5,
                      ...(item.text === "Log Out" && { color: tokens.error }),
                    }}
                  >
                    <Box sx={{ color: item.text === "Log Out" ? "inherit" : tokens.accent }}>
                      {item.icon}
                    </Box>
                    {item.text}
                  </MenuItem>
                ))
            ) : (
              <MenuItem
                onClick={() => {
                  handleMobileMenuClose();
                  navigate("/login");
                }}
              >
                Sign in
              </MenuItem>
            )}
          </Menu>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
